import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CakeSlice, CheckCircle2, Home, Sparkles, UtensilsCrossed } from "lucide-react";

import { clearCart } from "../../cart/useCart";
import { clearCheckoutContext } from "../../checkout/checkoutContext";
import { fetchCheckoutOrderSummary } from "../../checkout/checkoutPublicApi";
import SystemPageShell from "./SystemPageShell";


function formatRequestedDelivery(order) {
  if (!order?.requested_delivery_date || !order?.requested_delivery_window) return "";
  const date = new Date(`${order.requested_delivery_date}T${order.requested_delivery_window}:00`);
  if (Number.isNaN(date.getTime())) return `${order.requested_delivery_date} at ${order.requested_delivery_window}`;
  return `${date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} at ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function money(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
}

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const checkoutToken = params.get("token");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    document.title = "Order Confirmed | SugaLumps";
  }, []);

  useEffect(() => {
    if (!orderId || !checkoutToken) {
      setState("missing");
      return undefined;
    }
    let attempts = 0;
    let canceled = false;
    async function load() {
      try {
        const data = await fetchCheckoutOrderSummary(orderId, checkoutToken);
        if (canceled) return;
        setOrder(data);
        if (data.payment_status === "paid") {
          clearCart();
          clearCheckoutContext();
          setState("confirmed");
          return;
        }
        attempts += 1;
        setState("processing");
        if (attempts < 8) window.setTimeout(load, 1500);
      } catch {
        if (!canceled) setState("error");
      }
    }
    load();
    return () => { canceled = true; };
  }, [orderId, checkoutToken]);

  const confirmed = state === "confirmed";
  return (
    <SystemPageShell
      eyebrow="Order Confirmed"
      title={confirmed ? "Your sweets are on the way!" : "Confirming your order"}
      copy={confirmed ? "Thank you for your order. We received your payment and your order is now confirmed." : "We are checking the backend payment confirmation. This usually only takes a moment."}
      icon={confirmed ? <CheckCircle2 size={38} strokeWidth={1.7} /> : <CakeSlice size={38} strokeWidth={1.5} />}
      actions={(
        <>
          <Link className="sl-system-button primary" to="/"><Home size={16} /> Return Home</Link>
          <Link className="sl-system-button secondary" to="/menu"><UtensilsCrossed size={16} /> View Menu</Link>
        </>
      )}
    >
      {order && (
        <div className="sl-system-details">
          <div className="sl-system-detail-row"><span>Order</span><strong>{order.order_number}</strong></div>
          {order.customer_email && <div className="sl-system-detail-row"><span>Email</span><strong>{order.customer_email}</strong></div>}
          {order.delivery_address_summary && <div className="sl-system-detail-row"><span>Delivery</span><strong>{order.delivery_address_summary}</strong></div>}
          {formatRequestedDelivery(order) && <div className="sl-system-detail-row"><span>Delivery scheduled for</span><strong>{formatRequestedDelivery(order)}</strong></div>}
          {order.delivery_drive_minutes && <div className="sl-system-detail-row"><span>Estimated drive time</span><strong>{order.delivery_drive_minutes} minutes</strong></div>}
          <div className="sl-system-detail-row"><span>Total</span><strong>${money(order.total_amount)}</strong></div>
        </div>
      )}
      {state === "processing" && <p className="sl-system-note"><Sparkles size={14} aria-hidden="true" /> Payment is processing. This page will update when Stripe confirms it.</p>}
      {state === "missing" && <p className="sl-system-note">We could not find the confirmation reference for this order.</p>}
      {state === "error" && <p className="sl-system-note">We could not load order details. Please contact us if you need help.</p>}
      {confirmed && order?.email_sent && <p className="sl-system-note">A confirmation has been sent to your email.</p>}
    </SystemPageShell>
  );
}
